import { DataCategoryApi } from '@wiz-api/data-category-management'
import { BuildingApi, FloorApi } from '@wiz-api/personnel-position'
import { ClipApi } from '@wiz-api/scene-management'
import { IOTDeviceApi, Tag3DCameraPositionsApi } from '@wiz-api/iot-device-management'
import { SecurityAlarmRecordApi } from '@wiz-api/security-management'
import {
  map,
  get,
  find,
  isEmpty,
  includes,
  concat,
  forEach,
  keyBy,
  differenceBy,
  cloneDeep,
} from 'lodash'
import { Tag3DPositionApi, Tag3DPositionImgApi } from '@wiz-api/tag-3d-position-management'
import { instance as cloudRender } from 'wiz-cloud-render'

const dataCategoryApi = new DataCategoryApi()
const buildingApi = new BuildingApi()
const clipApi = new ClipApi()
const iotDeviceApi = new IOTDeviceApi()
const securityAlarmRecordApi = new SecurityAlarmRecordApi()
const tag3DPositionManagementAPI = new Tag3DPositionApi()
const tag3DPositionImgApi = new Tag3DPositionImgApi()
const tag3DCameraPositionsApi = new Tag3DCameraPositionsApi()
const floorApi = new FloorApi()

const MAXLOD =
  Wiz.globalSettings.dynamicModules.modules['visualization-cockpit-tongre']?.config
    .cameraDistanceConfig?.maxLod || 0
const MINLOD =
  Wiz.globalSettings.dynamicModules.modules['visualization-cockpit-tongre']?.config
    .cameraDistanceConfig?.minLod || 0

const headerComponentCode =
  Wiz.globalSettings.dynamicModules.modules['visualization-cockpit-tongre']?.config
    .headerComponentCode

class DeviceList {
  // 标签定位位置类型
  static markPosType = {
    leftTop: 0,
    midTop: 1,
    rightTop: 2,
    leftBottom: 3,
    midBottom: 4,
    rightBottom: 5,
  }

  constructor(
    propDeviceType = { name: '', value: '' },
    alarmCameraList = [],
    partialNoAlarmCameraList = [],
    isCustomEventName = 'openYSCameraControl'
  ) {
    // 驾驶舱和可视化都调用了此方法，目前驾驶舱都可以自定义名称isCustomEventName
    // 可视化自定义名称默认为openYSCameraControl
    this.deviceType = propDeviceType
    // 设备列表
    this.deviceTypeCode = ''
    this.deviceList = []
    this.deviceIconList = []
    this.textMarkerIds = [] // 摄像头对应的名称文本标签
    this.drawHandler = null
    this.player = null
    this.eventFn = null
    this.alarmCameraList = alarmCameraList
    this.isShowAlarm = !isEmpty(alarmCameraList)
    this.partialNoAlarmCameraList = partialNoAlarmCameraList
    this.isCustomEventName = isCustomEventName // 路由公用了相同页面
    this.deviceTypeName = ''
    this.iconTypeList = []
    this.basicImgType = ''
    this.basicIconList = []
    this.buildingList = [] // 建筑列表
    this.buildAabbList = [] // 建筑对应的aabb列表
    this.devicePositionMap = {} // 监控位置（键为标签id，值为位置）
    this.timer = null // 轮询查询相机实时位置timer
    this.nowIsIn = null // 现在相机位置是否处于室内
    this.inRoomDeviceList = [] // 处于室内的监控列表（存标签id）
    this.outRoomDeviceList = [] // 处于室外的监控列表（存标签id）
  }

  // 打开详情页面（待定）
  openDeviceDetail(deviceId) {
    if (!deviceId) {
      return
    }
    this.moveToArea(deviceId)
  }

  // 获取设备类型
  async getDeviceType() {
    const [err, res] = await tag3DPositionManagementAPI.getTag3DPositionTagTypeList()
    if (!err) {
      const deviceTypeList = res || []
      const queryName = 'IOTDeviceCamera'
      const findItem = deviceTypeList.find((n) => {
        return n.code.includes(queryName)
      })
      this.deviceTypeName = (findItem && findItem.code) || queryName
    }
  }

  // 数据字典获取图标类型
  async getIconCategoryByCodes() {
    const [err, res] = await dataCategoryApi.getListByCodes({
      codes: ['Tag3DPositionImgType'],
      includeDetails: true,
    })
    if (!err) {
      this.iconTypeList = res[0] && res[0].dataItems
      const findBasicItem = find(this.iconTypeList, (item) => {
        return item.code.toLowerCase() === 'base'
      })
      this.basicImgType = findBasicItem && findBasicItem.id
    }
  }
  // 获取基础图标列表
  async getBasicIconList() {
    if (this.basicImgType && this.deviceTypeName) {
      const [err, res] = await tag3DPositionImgApi.findPagedTag3DPositionImgsList({
        imgType: this.basicImgType, // 必传
        tagTypeCode: this.deviceTypeName, // 必传
        skipCount: 0,
        maxResultCount: 1000,
      })
      if (!err) {
        this.basicIconList = res && res.items
      }
    }
  }
  // 获取已标记的摄像头数据
  // eslint-disable-next-line class-methods-use-this
  async getCamerasHasPosition() {
    const [err, res] = await tag3DCameraPositionsApi.getCamerasHasPosition()
    if (!err) {
      const camerasHasPositionList = (res || []).map((item) => {
        return {
          id: item.tag3DPositionRelationKey,
          tagFileImgId: item.tag3DPositionDto?.tagFileImgId,
        }
      })
      return camerasHasPositionList
    }
    return []
  }
  // 获取正在报警摄像头
  // eslint-disable-next-line class-methods-use-this
  async getAlarmList() {
    if (headerComponentCode === 'CockpitHeaderDj') return []
    const [err, resulst] = await securityAlarmRecordApi.getDevicePositionWithWarning({
      isPosition: true,
      deviceTypeCode: 'Camera',
      isAlarm: true,
    })
    if (!err && resulst) {
      return resulst
    }
    return []
  }
  // 获取设备列表
  async getDeviceList(sceneId) {
    if (!this.deviceTypeCode) this.deviceTypeCode = await this.getCategoryByCodes()
    const [err, res] = await iotDeviceApi.getDeviceWithDetailPageList({
      // 暂时不做场景关联
      // modelId: sceneId,
      isPosition: true,
      deviceTypeCode: this.deviceTypeCode,
      skipCount: 0,
      maxResultCount: 999,
    })
    if (!err && res) {
      if (this.deviceType.value === 'camera') {
        const camerasHasPositionList = await this.getCamerasHasPosition()
        const camerasHasPositionMap = keyBy(camerasHasPositionList, 'id')
        const alarmList = await this.getAlarmList()
        const alarmMap = keyBy(alarmList, 'deviceId')
        // 获取已经展示过的摄像头数据
        const str = sessionStorage.getItem('cameraMarkHasShowedList')
        let result = res.items ?? []
        if (str && JSON.parse(str).length) {
          const strMap = keyBy(JSON.parse(str), 'cameraId')
          result = result.filter((item) => {
            return !strMap[item.id]
          })
        }
        return result.map((item) => {
          // 获取自定义图标id
          const objInfo = camerasHasPositionMap[item.id]
          // 获取是否报警
          const alarmInfo = alarmMap[item.id]
          return {
            ...item,
            tagFileImgId: objInfo?.tagFileImgId ?? '',
            isAlarm: alarmInfo?.isAlarm ?? false,
          }
        })
      }
      return res.items
    }
    return []
  }
  // 绘制图标
  async drawMarker(deviceDetail) {
    if (!this.player?.Native) return
    const backgroundUrl = `data://../apps/markers/${
      this.isShowAlarm || deviceDetail.isAlarm ? 'alarmCamera' : this.deviceType.value
    }.png`
    // const imgId = await this.player.Native.GisMarker.loadImage(backgroundUrl)
    const pictureOption = {
      id: deviceDetail.id,
      pos: JSON.parse(deviceDetail.threeDPosition),
      backgroundUrl,
      visible: true,
      nodeOffset: [0, 0],
      // imgId,
      imgSize: [32, 46],
      markPosType: DeviceList.markPosType.midBottom,
    }
    if ((MINLOD || MINLOD === 0) && (MAXLOD || MAXLOD === 0) && !(MAXLOD === 0 && MINLOD === 0)) {
      pictureOption.LodRange = [MINLOD, MAXLOD, 0]
    }
    const picRes = await cloudRender.Marker.createByPicture(pictureOption)
    if (picRes.status) {
      this.deviceIconList = concat(this.deviceIconList, picRes.data)
      this.devicePositionMap[deviceDetail.id] = JSON.parse(deviceDetail.threeDPosition)
      this.deviceType.value === 'camera' && this.createTextMakers(deviceDetail, picRes.data)
    }
    // this.player.Native.GisMarker.create(pictureOption, (markerId) => {
    //   this.deviceIconList.push({
    //     id: item.id,
    //     markerId,
    //     cameraMarkerId,
    //   })
    // })
  }

  // 绘制文本标记
  async createTextMakers(item, [cameraMarkerId]) {
    this.player.Native.GisMarker.loadImage('rgba:0,0,0,135', (id) => {
      const nameLength = item.name?.length
      const textRows = Math.ceil((nameLength * 15) / 150)
      const imgHeight = textRows * 15 + 10
      const option = {
        pos: JSON.parse(item.threeDPosition), // 定位点，世界坐标，三维向量
        nodeOffset: [-60, -80], // 相对定位点的偏移量，二维向量
        imgId: id, // 图片id
        imgSize: [textRows > 1 ? 160 : nameLength * 15, imgHeight || 30],
        text: item.name || '', // 文本
        textFontSize: 15.0, // 字体大小
        breakWidth: 150, // 最大行宽，超过宽度会换行，-1表示限制，换行符（'\n'）会强制换行不受控制
        textColor: 0xffffffff, // 文本颜色
        title: null, // 标题
        contentPadding: [5, 5, 5, 5],
        visible: false,
      }
      if (MAXLOD) option.maxLod = MAXLOD
      if (MINLOD) option.minLod = MINLOD
      this.player.Native.GisMarker.create(option, (markerId) => {
        this.textMarkerIds.push({
          id: item.id,
          markerId,
          cameraMarkerId,
        })
      })
    })
  }

  // 标签事件
  async markerClick(e) {
    // 把id传递出去，自己判断是否是报警，然后弹出对应弹窗（openYSCameraControl这个事件不管）
    // 左键点击并且有id
    if (e.type === 1 && !isEmpty(e.markerId)) {
      const deviceId = find(this.deviceIconList, (item) => {
        return item === e.markerId
      })
      if (deviceId) {
        // 移动到视角
        this.openDeviceDetail(deviceId)
      }
      const targetItem = find(this.deviceList, (item) => {
        return item.id === e.markerId
      })
      if (
        this.deviceType.value === 'access' &&
        deviceId &&
        this.isCustomEventName === 'openYSCameraControl'
      ) {
        // 只针对门禁做修改，不改变其他操作
        DeviceList.OpenAccessRecord({
          deviceId: targetItem.id,
          deviceType: targetItem.deviceType,
        })
      } else if (
        this.deviceType.value === 'barrier' &&
        deviceId &&
        this.isCustomEventName === 'openYSCameraControl'
      ) {
        // 打开道闸
        this.openBarrierRecord({
          deviceId: targetItem.id,
          deviceType: targetItem.deviceType,
        })
      } else {
        // 报警中的摄像头打开预警弹窗
        if (targetItem?.isAlarm) {
          const [err, res] = await securityAlarmRecordApi.getWarningCard({
            code: targetItem?.deviceCode,
          })
          if (!err && res) {
            const openEvent = new CustomEvent('openComponent', {
              detail: {
                toCom: {
                  code: 'EarlyWarningDisposalCard',
                  origin: 'tag',
                  name: '预警处置卡',
                },
                position: 'left',
                type: 'tags',
                props: {
                  ...res,
                },
              },
            })
            window.dispatchEvent(openEvent)
          }
          return
        }
        if (targetItem && this.deviceType.value === 'camera' && targetItem.code) {
          this.isCustomEventName = 'openVideoDialog'
          this.destroyAllLableOmitOne(targetItem)
        }
      }
    }
    // 鼠标移入标签 - 展示摄像头名称
    if (e.type === 2 && !isEmpty(e.markerId) && this.deviceType.value === 'camera') {
      const textMarkerId = this.textMarkerIds.find((item) => {
        return item.cameraMarkerId === e.markerId
      })?.markerId
      this.player.Native.GisMarker.update(textMarkerId, {
        visible: true, // 显示
      })
    }
  }
  // 鼠标移入
  markerHover = (e) => {
    if (e.markerId === 0 && this.deviceType.value === 'camera') {
      // 隐藏文本标记
      forEach(this.textMarkerIds, (item) => {
        this.player.Native.GisMarker.update(item.markerId, {
          visible: false, // 隐藏
        })
      })
    }
  }
  // 打开门禁列表，及人员4出入记录
  static OpenAccessRecord(data) {
    const openEvent = new CustomEvent('openComponent', {
      detail: {
        toCom: {
          code: 'AccessDoorControlGateList',
          origin: 'tag',
          name: '门禁列表',
        },
        position: 'left',
        type: 'tags',
        props: {
          ...data,
        },
      },
    })
    window.dispatchEvent(openEvent)
  }
  // 打开道闸列表，及车辆出入记录
  // eslint-disable-next-line class-methods-use-this
  openBarrierRecord(data) {
    const openEvent = new CustomEvent('openComponent', {
      detail: {
        toCom: {
          code: 'AccessControlGateList',
          origin: 'tag',
          name: '道闸列表',
        },
        position: 'left',
        type: 'tags',
        props: {
          ...data,
        },
      },
    })
    window.dispatchEvent(openEvent)
  }
  // 摄像头打开
  destroyAllLableOmitOne(detail) {
    // 如果没有打开对应功能标签，则打开'openYSCameraControl'
    const filterEvent = new CustomEvent(this.isCustomEventName, {
      detail,
    })
    window.dispatchEvent(filterEvent)
  }

  // 获取包围盒数据
  async handleBoxList() {
    const [err1, res1] = await clipApi
      .getList({ skipCount: 0, maxResultCount: 9999 })
      .catch((e) => {
        return e
      })
    if (!err1 && res1.items?.length && res1.totalCount > 0) {
      forEach(res1.items, (item) => {
        if (item.minimumLocation && item.maximumLocation) {
          const min = cloneDeep(JSON.parse(item.minimumLocation))
          const max = cloneDeep(JSON.parse(item.maximumLocation))
          this.buildAabbList.push([
            [min.x, min.y, min.z],
            [max.x, max.y, max.z],
          ])
        }
      })
    }
  }

  // 获取建筑列表以及aabb
  async getBuildingList(sceneId) {
    const [err, res] = await buildingApi.getList({
      sceneId,
      skipCount: 0,
      maxResultCount: 1000,
    })
    if (!err && res) {
      this.buildingList = res.items.map((item) => {
        return item.id
      })
      for (let index = 0; index < this.buildingList.length; index += 1) {
        const buildingId = this.buildingList[index]
        // let floorList = []

        // 获取楼层列表
        // eslint-disable-next-line no-await-in-loop
        // const [err2, res2] = await floorApi.getList({
        //   sceneId,
        //   buildingId,
        //   skipCount: 0,
        //   maxResultCount: 1000,
        // })
        // if (!err2 && !isEmpty(res2)) {
        //   floorList = res.items
        // }

        // 获取建筑的 aabb 信息
        // todo： 通过建筑 id 获取楼层 id
        // eslint-disable-next-line no-await-in-loop
        const [err1, res1] = await clipApi.get(buildingId)
        if (!err1 && !isEmpty(res1)) {
          const min = cloneDeep(JSON.parse(res1.minimumLocation))
          const max = cloneDeep(JSON.parse(res1.maximumLocation))
          this.buildAabbList.concat(this.buildAabbList, [
            [min.x, min.y, min.z],
            [max.x, max.y, max.z],
          ])
        }
      }
    }
  }

  /**
   * 判断点是否在包围盒内
   * @param {Array} point - 待检测的点坐标 [x, y, z]
   * @param {Array} boundingBox - 包围盒 [[minX, minY, minZ], [maxX, maxY, maxZ]]
   * @returns {Boolean} - 如果点在包围盒内则返回true，否则返回false
   */
  // eslint-disable-next-line class-methods-use-this
  isPointInBoundingBox(point, boundingBox) {
    const [minPoint, maxPoint] = boundingBox
    const [x, y, z] = point || []
    const [minX, minY, minZ] = minPoint
    const [maxX, maxY, maxZ] = maxPoint

    return x >= minX && x <= maxX && y >= minY && y <= maxY && z >= minZ && z <= maxZ
  }

  /**
   * 判断点是否和当前相机一起在包围盒内
   * @param {Array} point - 相机的点坐标 [x, y, z]
   * @param {Array} point - 待检测的点坐标 [x, y, z]
   * @param {Array} boundingBox - 包围盒 [[minX, minY, minZ], [maxX, maxY, maxZ]]
   * @returns {Boolean} - 如果点和相机都在包围盒内则返回true，否则返回false
   */
  async isPointInBoundingBoxWithCamera(cameraEyes, point, boundingBox) {
    return (
      this.isPointInBoundingBox(cameraEyes, boundingBox) &&
      this.isPointInBoundingBox(point, boundingBox)
    )
  }

  // 处理室内外摄像头显隐逻辑
  async divideOutAndIn() {
    // 轮询获取当前相机位置
    this.timer = setInterval(async () => {
      // 判断相机位于室内还是室外
      const cameraEyes = (await cloudRender.Camera.getCurrentCamera()).data.eye
      let isCameraInside = false
      let buildingOfCamera = null // 当前相机所在建筑
      this.buildAabbList.forEach((item) => {
        if (this.isPointInBoundingBox(cameraEyes, item)) {
          isCameraInside = true
          buildingOfCamera = item
        }
      })
      // 摄像头位置与上一次相同不做处理
      if (this.nowIsIn === isCameraInside) {
        return
      }
      this.nowIsIn = isCameraInside

      // 先隐藏所有监控标签，统一处理
      await this.hidden()
      // 区分出在室内外的监控，判断放在每次创建图表后
      this.buildAabbList.forEach((build) => {
        this.deviceIconList.forEach((id) => {
          if (this.isPointInBoundingBox(this.devicePositionMap[id], build)) {
            this.inRoomDeviceList = concat(this.inRoomDeviceList, id)
            this.outRoomDeviceList.includes(id) &&
              this.outRoomDeviceList.splice(this.outRoomDeviceList.indexOf(id), 1)
          } else {
            this.outRoomDeviceList = concat(this.outRoomDeviceList, id)
            this.inRoomDeviceList.includes(id) &&
              this.inRoomDeviceList.splice(this.inRoomDeviceList.indexOf(id), 1)
          }
        })
      })
      if (isCameraInside) {
        // 相机处于室内，则找出与相机同处室内的标签显示
        this.inRoomDeviceList.forEach((id) => {
          if (
            this.isPointInBoundingBoxWithCamera(
              cameraEyes,
              this.devicePositionMap[id],
              buildingOfCamera
            )
          ) {
            cloudRender.Marker.updateByPicture({
              id,
              visible: true,
            })
          }
        })
      } else {
        // 相机处于室外，则显示所有处于室外的标签
        this.outRoomDeviceList.forEach((id) => {
          cloudRender.Marker.updateByPicture({
            id,
            visible: true,
          })
        })
      }
    }, 1000)
  }

  // 创建设备
  async create(propPlayer, sourceId) {
    this.player = propPlayer
    if (!this.player?.Native) {
      return
    }
    // 获取建筑列表
    // await this.getBuildingList(sourceId)
    // 获取包围盒数据
    await this.handleBoxList()
    // /** 低代码环境模拟数据开始 */
    // // 假设所有建筑的aabb列表
    // this.buildAabbList = [[
    //   [
    //     -4.30000114440918,
    //     -3.9900002479553223,
    //     -4
    //   ],
    //   [
    //     186,
    //     113.20150756835938,
    //     80.84226989746094
    //   ]
    // ]]
    // for (let i = 0; i < 3; i += 1) {
    //   // 随机（可能有室内）
    //   const randomId = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10)
    //   const randomPos = [
    //     Math.random() * 300, // x: 0~300
    //     Math.random() * 400, // y: 0~400
    //     Math.random() // z: 0~1
    //   ]
    //   // 室外
    //   const roomId = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10)
    //   const roomPos = [Math.random() * 150 + 20, 30.9991912841797, 20]
    //   // eslint-disable-next-line no-await-in-loop
    //   const outRoom = await cloudRender.Marker.createByPicture({
    //     id: randomId, // 生成随机ID字符串
    //     pos: randomPos,
    //     backgroundUrl: 'data://../apps/markers/camera.png',
    //     visible: false,
    //     nodeOffset: [0, 0],
    //     imgSize: [32, 46],
    //     markPosType: 4,
    //   })
    //   // 房内
    //   // eslint-disable-next-line no-await-in-loop
    //   const inRoom = await cloudRender.Marker.createByPicture({
    //     id: roomId,
    //     pos: roomPos,
    //     backgroundUrl: 'data://../apps/markers/camer@a.png',
    //     visible: false,
    //     nodeOffset: [0, 0],
    //     imgSize: [32, 46],
    //     markPosType: 4,
    //   })
    //   if (inRoom.status) {
    //     this.deviceIconList = concat(this.deviceIconList, inRoom.data)
    //     this.devicePositionMap[roomId] = roomPos
    //     // this.deviceType.value === 'camera' && this.createTextMakers(deviceDetail, inRoom.data)
    //   }
    //   if (outRoom.status) {
    //     this.deviceIconList = concat(this.deviceIconList, outRoom.data)
    //     this.devicePositionMap[randomId] = randomPos
    //     // this.deviceType.value === 'camera' && this.createTextMakers(deviceDetail, outRoom.data)
    //   }
    // }
    // /** 模拟数据结束 */
    await this.getDeviceType()
    await this.getIconCategoryByCodes()
    await this.getBasicIconList()
    // 生成设备
    this.drawHandler = new Promise(async (resolve) => {
      // 获取设备列表数据
      if (this.isShowAlarm) {
        this.deviceList = this.alarmCameraList
      } else if (this.partialNoAlarmCameraList.length > 0) {
        this.deviceList = this.partialNoAlarmCameraList
      } else {
        this.deviceList = await this.getDeviceList(sourceId)
      }
      await Promise.all(
        map(this.deviceList, (item) => {
          return this.drawMarker(item)
        })
      )
      const markerList = map(this.deviceList, (item) => {
        return {
          id: item.id,
          threeDPosition: item.threeDPosition,
        }
      })
      sessionStorage.setItem(`${this.deviceType.value}MarkList`, JSON.stringify(markerList))
      resolve()
    })
    this.initClickEvent()
    this.player && this.player.Native.GisMarker.EventGisMarkerHover.connect(this.markerHover)
    this.divideOutAndIn()
  }

  // 销毁设备
  async destroy() {
    sessionStorage.removeItem(`${this.deviceType.value}MarkList`)
    if (!this.player?.Native) {
      return
    }
    // 清除获取相机位置的定时器
    clearInterval(this.timer)
    this.timer = null
    this.nowIsIn = null
    // await this.drawHandler
    if (!get(cloudRender, 'Marker')) return
    // 销毁图标
    cloudRender.Marker.destroyMarker(this.deviceIconList)
    this.deviceIconList = []
    this.devicePositionMap = {}
    this.deviceList = []
    this.delTextMarkers()
    // 取消标签的事件监听
    this.initClickEvent(false)
    this.player && this.player.Native.GisMarker.EventGisMarkerHover.disconnect(this.markerHover)
  }

  // 删除名称标记
  delTextMarkers() {
    if (!this.player || !this.player.Native) return
    forEach(this.textMarkerIds, (item) => {
      this.player.Native.GisMarker.destroy(item.markerId)
    })
    this.textMarkerIds = []
  }

  initClickEvent(isDestroyed = true) {
    if (!this.eventFn) {
      this.eventFn = (...args) => {
        this.markerClick(...args)
      }
    }
    if (!isDestroyed) {
      cloudRender.Dispatch.off('Marker', this.eventFn)
      return
    }
    cloudRender.Dispatch.on('Marker', this.eventFn)
  }

  // 切换标签显示隐藏
  async toggleDisplay(type) {
    if (!this.player?.Native) {
      return
    }
    await this.drawHandler
    await cloudRender.Marker.updateByPicture(
      map(this.deviceIconList, (i) => {
        return {
          id: i,
          visible: type,
        }
      })
    )
  }

  // 移动视角
  async moveToArea(deviceId) {
    const deviceInfo = find(this.deviceList, ['id', deviceId])
    const treeData = await this.player?.Native.ModelTree.getRootItems()
    this.player?.Native.Camera.moveToPos(
      JSON.parse(deviceInfo.threeDPosition),
      treeData[0]?.nodePtr,
      50,
      2
    )
  }

  // 隐藏设备及其标签
  async hidden() {
    await this.toggleDisplay(false)
  }

  // 重新显示设备及其标签
  async display() {
    await this.toggleDisplay(true)
  }

  // 标签报警
  async update(isAlarm, alarmList) {
    if (isAlarm) this.alarmCameraList = alarmList
    const backgroundUrl = `data://../apps/markers/${
      isAlarm ? 'alarmCamera' : this.deviceType.value
    }.png`
    await Promise.all(
      map(this.alarmCameraList, (item) => {
        return cloudRender.Marker.updateByPicture({
          id: item.id,
          backgroundUrl,
        })
      })
    )
  }

  // 获取设备code
  async getCategoryByCodes() {
    const [err, res] = await dataCategoryApi.getListByCodes({
      codes: ['IOTDeviceTemplateDeviceType'],
      includeDetails: true,
    })
    if (!err) {
      const deviceTypeList = get(
        find(res, ['code', 'IOTDeviceTemplateDeviceType']),
        'dataItems',
        []
      )
      return get(
        find(deviceTypeList, (item) => {
          return includes(item.displayName, this.deviceType.name)
        }),
        'code',
        ''
      )
    }
    return ''
  }
}

export default DeviceList
